import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from 'react-bootstrap';
import { Search } from 'components';
import placeholderImage from 'assets/img/covers/placeholder.jpg';
import { useGetRegionPageAvailableParticipants } from 'api/regions/hooks';
import { useSelectedProfileStore } from 'stores/selectedProfileStore';
import { useFetchRetailerPageAvailableParticipants, useFetchStore } from 'api/store/hooks';
import { RETAILER } from 'constants/roles';
import { useAuthStore } from 'stores/authStore';
import * as Ui from './SelectRetailersModal.styles';

const getDisplayName = (retailer) => {
  const parts = [retailer.name, retailer.last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
};

const SelectRetailersModal = ({ isOpen, onClose, onConfirm, initialSelected = [] }) => {
  const { t } = useTranslation('pages', { keyPrefix: 'widgets' });
  const { t: tApp } = useTranslation('app');

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(initialSelected);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelected(initialSelected);
    }
  }, [initialSelected, isOpen]);

  const isRetailer = useAuthStore((state) => state.role) === RETAILER;
  const regionUid = useSelectedProfileStore((state) => state.selectedRegionUid);
  const { data: store } = useFetchStore({ enabled: isRetailer });
  const { data: regionRetailers = [], isLoading: isRegionLoading } =
    useGetRegionPageAvailableParticipants(isRetailer ? undefined : regionUid);
  const { data: storeRetailers = [], isLoading: isStoreLoading } =
    useFetchRetailerPageAvailableParticipants(store?.uid, {
      enabled: isRetailer && !!store?.uid,
    });
  const retailers = isRetailer ? storeRetailers : regionRetailers;
  const isLoading = isRetailer ? isStoreLoading : isRegionLoading;

  const selectedUids = useMemo(() => new Set(selected.map((r) => r.uid)), [selected]);

  const filteredRetailers = useMemo(() => {
    let list = retailers;

    if (showSelectedOnly) {
      list = list.filter((r) => selectedUids.has(r.uid));
    }

    if (search.trim()) {
      const query = search.toLowerCase();
      list = list.filter((r) => {
        const name = getDisplayName(r)?.toLowerCase() || '';
        const vanity = (r.vanity_url || '').toLowerCase();
        return name.includes(query) || vanity.includes(query);
      });
    }

    return list;
  }, [retailers, search, showSelectedOnly, selectedUids]);

  const handleToggle = (retailer) => {
    if (selectedUids.has(retailer.uid)) {
      setSelected((prev) => prev.filter((r) => r.uid !== retailer.uid));
    } else {
      setSelected((prev) => [...prev, retailer]);
    }
  };

  const handleConfirm = () => {
    onConfirm(selected);
    onClose();
  };

  const handleClose = () => {
    setSelected(initialSelected);
    setShowSelectedOnly(false);
    onClose();
  };

  return (
    <Ui.StyledModal
      open={isOpen}
      header={{ title: t('selectRetailers') }}
      closeOnDocumentClick
      onClose={handleClose}
      bodyStyle={{
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'hidden',
        paddingBottom: 0,
      }}
      footer={() => (
        <Ui.Footer>
          <Button variant="white" onClick={handleClose} className="rounded">
            {tApp('cancel')}
          </Button>
          <Button
            variant="primary"
            className="rounded"
            disabled={selected.length === 0}
            onClick={handleConfirm}
          >
            {tApp('confirm')} ({selected.length})
          </Button>
        </Ui.Footer>
      )}
    >
      <Ui.ModalBody>
        <Ui.Toolbar>
          <Ui.SearchWrapper>
            <Search
              value={search}
              onChange={setSearch}
              placeholder={t('searchRetailersPlaceholder')}
            />
          </Ui.SearchWrapper>
          <Ui.FilterButton
            type="button"
            $active={showSelectedOnly}
            onClick={() => setShowSelectedOnly((v) => !v)}
          >
            {t('selectedRetailersCount', { count: selected.length })}
          </Ui.FilterButton>
        </Ui.Toolbar>
        <Ui.ScrollableWrapper>
          <Ui.RetailersGrid>
            {isLoading ? (
              <Ui.EmptyState>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              </Ui.EmptyState>
            ) : filteredRetailers.length === 0 ? (
              <Ui.EmptyState>{t('noRetailersFound')}</Ui.EmptyState>
            ) : (
              filteredRetailers.map((retailer) => {
                const isSelected = selectedUids.has(retailer.uid);
                const displayName = getDisplayName(retailer);
                return (
                  <Ui.RetailerCard
                    key={retailer.uid}
                    $selected={isSelected}
                    onClick={() => handleToggle(retailer)}
                  >
                    <Ui.RetailerImage
                      src={retailer.picture || placeholderImage}
                      alt={displayName || retailer.vanity_url}
                    />
                    <Ui.RetailerInfo>
                      {displayName && <Ui.RetailerName>{displayName}</Ui.RetailerName>}
                      <Ui.RetailerEmail>{retailer.vanity_url}</Ui.RetailerEmail>
                    </Ui.RetailerInfo>
                  </Ui.RetailerCard>
                );
              })
            )}
          </Ui.RetailersGrid>
        </Ui.ScrollableWrapper>
      </Ui.ModalBody>
    </Ui.StyledModal>
  );
};

export default SelectRetailersModal;
